package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import com.appsyde.shared.app.navigation.AppPaths
import com.appsyde.shared.presentation.style.AppStylesLanding
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AppSydeLandingPage(
    onSignup: () -> Unit,
    onLogin: () -> Unit,
    isCheckingSession: Boolean = false
) {
    Style(AppStylesLanding)
    val ctaEnabled = !isCheckingSession

    Div({ classes(AppStylesLanding.page) }) {

        // NAV
        Nav({ classes(AppStylesLanding.topNav) }) {
            Div({ classes(AppStylesLanding.container, AppStylesLanding.navInner) }) {
                A("#", { classes(AppStylesLanding.brand) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesLanding.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Span { Text("AppSyde") }
                }

                Div({ classes(AppStylesLanding.navLinks) }) {
                    A("#how", { classes(AppStylesLanding.navLink) }) { Text("Comment ça marche") }
                    A("#features", { classes(AppStylesLanding.navLink) }) { Text("Fonctionnalités") }
                    A("#privacy", { classes(AppStylesLanding.navLink) }) { Text("Privacy-first") }
                    A("#faq", { classes(AppStylesLanding.navLink) }) { Text("FAQ") }
                }

                Div({ classes(AppStylesLanding.navCtas) }) {
                    Div({ classes(AppStylesLanding.landingNavCtasWrap) }) {
                        A(attrs = {
                            classes(AppStylesLanding.btn, AppStylesLanding.btnPrimary)
                            onClick { it.preventDefault(); if (ctaEnabled) onSignup() }
                        }) {
                            Text("Créer un compte")
                        }
                        A(attrs = {
                            classes(AppStylesLanding.btn, )
                            onClick { it.preventDefault(); if (ctaEnabled) onLogin() }
                        }) {
                            Text("Se connecter")
                        }
                    }
                }
            }
        }

        Hero(onSignup = onSignup, onLogin = onLogin, ctaEnabled = ctaEnabled)
        Difference()
        HowItWorks()
        Features()
        Privacy()
        FinalCta(onSignup = onSignup, onLogin = onLogin, ctaEnabled = ctaEnabled)
        Faq()
        FooterBlock()
    }
}

/* ------------------------------ HERO (simple & calm) ------------------------------ */

@Composable
private fun Hero(onSignup: () -> Unit, onLogin: () -> Unit, ctaEnabled: Boolean = true) {
    Section({ classes(AppStylesLanding.hero) }) {

        Div({ classes(AppStylesLanding.aurora) }) {
            Div({ classes(AppStylesLanding.blob1) })
            Div({ classes(AppStylesLanding.blob2) })
        }

        Div({ classes(AppStylesLanding.container) }) {
            Div({ classes(AppStylesLanding.heroInner) }) {

                Div({ classes(AppStylesLanding.eyebrow) }) {
                    Span({ classes(AppStylesLanding.dot) })
                    Text("Support par email — simple, cohérent, sous contrôle")
                }

                H1({ classes(AppStylesLanding.h1) }) {
                    Text("Un support email ")
                    Span({ classes(AppStylesLanding.gradientWord) }) { Text("plus cohérent") }
                    Text(" au fil du temps")
                }

                P({ classes(AppStylesLanding.subtitle) }) {
                    Text("AppSyde vous aide à répondre plus sereinement : il met en avant les infos utiles, signale ce qui manque, et prépare un brouillon que vous pouvez ajuster avant envoi.")
                }

                Div({ classes(AppStylesLanding.heroCtas) }) {
                    A(attrs = {
                        classes(AppStylesLanding.btn, AppStylesLanding.btnPrimary, )
                        onClick { it.preventDefault(); if (ctaEnabled) onSignup() }
                    }) {
                        Text("Créer un compte")
                    }
                    A("#how", { classes(AppStylesLanding.btn) }) {
                        Text("Voir comment ça marche")
                    }
                }

                Div({ classes(AppStylesLanding.helperLine) }) {
                    Text("Installation en 2 minutes · Aucune carte requise · Vous gardez la main sur chaque envoi")
                }

                Div({ classes(AppStylesLanding.heroSpacer) })
            }
        }
    }
}

/* ------------------------------ DIFFERENCE ------------------------------ */

@Composable
private fun Difference() {
    Section({ classes(AppStylesLanding.section) }) {
        Div({ classes(AppStylesLanding.container) }) {

            Div({ classes(AppStylesLanding.sectionHeader) }) {
                H2({ classes(AppStylesLanding.h2) }) { Text("AppSyde progresse à partir de ce que vous validez") }
                P({ classes(AppStylesLanding.sectionLead) }) {
                    Text("Les brouillons sont des suggestions. Les réponses que vous envoyez deviennent une référence interne, pour gagner en cohérence naturellement — sans réglages complexes.")
                }
            }

            Div({ classes(AppStylesLanding.grid3) }) {
                InfoCard(
                    icon = "⚡",
                    title = "Des brouillons prêts à relire",
                    body = "Une proposition claire, tout de suite. Vous modifiez si besoin, puis vous envoyez."
                )
                InfoCard(
                    icon = "🧠",
                    title = "Votre façon de répondre, respectée",
                    body = "Plus vous validez, plus AppSyde colle à votre ton et à vos habitudes de support."
                )
                InfoCard(
                    icon = "🔒",
                    title = "Une amélioration prudente",
                    body = "Pas de copier-coller d’emails clients. AppSyde retient des façons de répondre, pas des données brutes."
                )
            }
        }
    }
}

/* ------------------------------ HOW IT WORKS ------------------------------ */

@Composable
private fun HowItWorks() {
    Section({ id("how"); classes(AppStylesLanding.section) }) {
        Div({ classes(AppStylesLanding.container) }) {

            Div({ classes(AppStylesLanding.sectionHeader) }) {
                H2({ classes(AppStylesLanding.h2) }) { Text("Comment ça marche, concrètement") }
                P({ classes(AppStylesLanding.sectionLead) }) {
                    Text("Vous connectez votre boîte support, AppSyde prépare, vous décidez. Et avec le temps, il devient plus cohérent avec votre manière de répondre.")
                }
            }

            Div({ classes(AppStylesLanding.split) }) {

                Div({ classes(AppStylesLanding.steps) }) {
                    Step(1, "Connectez votre boîte Gmail") {
                        Text("Aucune migration, aucun import. Vous retrouvez vos conversations, simplement.")
                    }
                    Step(2, "AppSyde met le ticket en clair") {
                        Text("Il résume l’essentiel, extrait les infos utiles (numéro de commande, lieu…), et signale ce qui manque.")
                    }
                    Step(3, "Un brouillon est proposé") {
                        Text("Vous relisez, vous éditez, vous régénérez si besoin. Rien n’est envoyé sans vous.")
                    }
                    Step(4, "La cohérence s’améliore avec l’usage") {
                        Text("Les réponses que vous validez servent de référence pour proposer des réponses plus proches de votre style.")
                    }
                }

                Div({ classes(AppStylesLanding.card) }) {
                    Div({ classes(AppStylesLanding.cardTitleRow) }) {
                        Div({ classes(AppStylesLanding.iconBubble) }) { Text("🧩") }
                        H3({ classes(AppStylesLanding.cardTitle) }) { Text("Pourquoi ça marche”") }
                    }
                    P({ classes(AppStylesLanding.cardBody) }) {
                        Text("Le support change tout le temps : contexte, exceptions, demandes bizarres. AppSyde aide surtout à rester cohérent et à éviter les réponses hasardeuses.")
                    }
                    Div({ classes(AppStylesLanding.divider) })
                    P({ classes(AppStylesLanding.cardBody) }) {
                        Text("Résultat : moins d’erreurs, moins d’aller-retours, et une tonalité qui reste la vôtre.")
                    }
                    Div({ classes(AppStylesLanding.cardCtaWrap) }) {
                        A("#connect", { classes(AppStylesLanding.btn, AppStylesLanding.btnPrimary) }) {
                            Text("Tester sur ma boîte Gmail")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Step(n: Int, title: String, body: @Composable () -> Unit) {
    Div({ classes(AppStylesLanding.step) }) {
        Div({ classes(AppStylesLanding.stepNum) }) { Text(n.toString()) }
        Div {
            Div({ classes(AppStylesLanding.stepTitle) }) { Text(title) }
            P({ classes(AppStylesLanding.cardBody) }) { body() }
        }
    }
}

/* ------------------------------ FEATURES ------------------------------ */

@Composable
private fun Features() {
    Section({ id("features"); classes(AppStylesLanding.section) }) {
        Div({ classes(AppStylesLanding.container) }) {

            Div({ classes(AppStylesLanding.sectionHeader) }) {
                H2({ classes(AppStylesLanding.h2) }) { Text("Fonctionnalités utiles, pas décoratives") }
                P({ classes(AppStylesLanding.sectionLead) }) {
                    Text("Tout est pensé pour répondre vite et bien, sans transformer votre support en usine à gaz.")
                }
            }

            Div({ classes(AppStylesLanding.grid3) }) {
                InfoCard("⚡", "Brouillon immédiat", "Une réponse proposée dès l’ouverture du ticket.")
                InfoCard("✍️", "Édition & régénération", "Vous ajustez en quelques secondes, sans repartir de zéro.")
                InfoCard("🧭", "Infos manquantes", "AppSyde vous dit clairement ce qu’il faut demander avant de répondre.")
                InfoCard("🕰️", "Historique clair", "Le contexte reste lisible, même quand la discussion s’allonge.")
                InfoCard("👥", "Multi-agents léger", "Moins de doubles réponses, continuité plus simple dans une boîte partagée.")
                InfoCard("📊", "Mesures simples", "Quelques signaux utiles sans se noyer dans les KPI.")
            }
        }
    }
}

/* ------------------------------ PRIVACY ------------------------------ */

@Composable
private fun Privacy() {
    Section({ id("privacy"); classes(AppStylesLanding.section) }) {
        Div({ classes(AppStylesLanding.container) }) {

            Div({ classes(AppStylesLanding.sectionHeader) }) {
                H2({ classes(AppStylesLanding.h2) }) { Text("Privacy-first, sans sacrifier le produit") }
                P({ classes(AppStylesLanding.sectionLead) }) {
                    Text("Le but n’est pas de collecter vos emails. Le but est de mieux répondre, en restant prudent sur les données.")
                }
            }

            Div({ classes(AppStylesLanding.split) }) {
                InfoCard(
                    "🧱",
                    "Règle d’or : pas de réutilisation brute",
                    "AppSyde retient des façons de répondre, pas des contenus copiés-collés de clients."
                )
                InfoCard(
                    "🛟",
                    "Contrôle et prudence",
                    "Si une info est incertaine (date, délai, montant), AppSyde préfère demander ou vous alerter plutôt que d’inventer."
                )
            }
        }
    }
}

/* ------------------------------ FINAL CTA ------------------------------ */

@Composable
private fun FinalCta(onSignup: () -> Unit, onLogin: () -> Unit, ctaEnabled: Boolean = true) {
    Section({ id("connect"); classes(AppStylesLanding.section) }) {
        Div({ classes(AppStylesLanding.container) }) {
            Div({ classes(AppStylesLanding.card, AppStylesLanding.finalCtaCard) }) {
                Div({ classes(AppStylesLanding.sectionHeader) }) {
                    H2({ classes(AppStylesLanding.h2) }) { Text("Essayez sur votre boîte Gmail") }
                    P({ classes(AppStylesLanding.sectionLead) }) {
                        Text("Pas de démo abstraite. Connectez votre compte et traitez un premier ticket en conditions réelles.")
                    }
                }

                Div({ classes(AppStylesLanding.heroCtas) }) {
                    A(attrs = {
                        classes(AppStylesLanding.btn, AppStylesLanding.btnPrimary, )
                        onClick { it.preventDefault(); if (ctaEnabled) onSignup() }
                    }) {
                        Text("Créer un compte")
                    }
                    A("#faq", { classes(AppStylesLanding.btn) }) { Text("Lire la FAQ") }
                }

                Div({ classes(AppStylesLanding.helperLine) }) {
                    Text("Aucune carte requise · Désactivation en un clic · Vous gardez la main sur chaque envoi")
                }
            }
        }
    }
}

/* ------------------------------ FAQ ------------------------------ */

@Composable
private fun Faq() {
    Section({ id("faq"); classes(AppStylesLanding.section) }) {
        Div({ classes(AppStylesLanding.container) }) {

            Div({ classes(AppStylesLanding.sectionHeader) }) {
                H2({ classes(AppStylesLanding.h2) }) { Text("FAQ") }
                P({ classes(AppStylesLanding.sectionLead) }) {
                    Text("Les questions qui reviennent quand on parle de support par email… mais sérieusement.")
                }
            }

            Div({ classes(AppStylesLanding.faq) }) {
                FaqItem(
                    "Est-ce qu’AppSyde envoie des emails à ma place ?",
                    "Non. AppSyde prépare un brouillon. Vous relisez, vous modifiez si besoin, puis vous envoyez."
                )
                FaqItem(
                    "Qu’est-ce qui “s’améliore” exactement ?",
                    "La cohérence : la structure des réponses, la façon de demander les infos manquantes, et le ton global."
                )
                FaqItem(
                    "Vous réutilisez mes emails clients ?",
                    "Non. AppSyde n’a pas besoin de copier des emails. Il retient des patterns de réponse, de manière minimale et contrôlée."
                )
                FaqItem(
                    "Est-ce qu’il faut tout configurer ?",
                    "Non. Ça fonctionne dès le départ. Et ça devient plus cohérent au fil des validations."
                )
            }
        }
    }
}

@Composable
private fun FaqItem(q: String, a: String) {
    Div({ classes(AppStylesLanding.faqItem) }) {
        Div({ classes(AppStylesLanding.faqQ) }) { Text(q) }
        P({ classes(AppStylesLanding.cardBody) }) { Text(a) }
    }
}

/* ------------------------------ FOOTER ------------------------------ */

@Composable
private fun FooterBlock() {
    Footer({ classes(AppStylesLanding.footer) }) {
        Div({ classes(AppStylesLanding.container) }) {
            Div({ classes(AppStylesLanding.footerInner) }) {
                    Div({ classes(AppStylesLanding.footerBrandRow) }) {
                        Img("/appsydeIcon.png", attrs = {
                            classes(AppStylesLanding.brandIcon)
                            attr("alt", "AppSyde")
                        })
                        Span { Text("© ${js("new Date().getFullYear()") as Int} AppSyde") }
                }
                Div({ classes(AppStylesLanding.footerLinks) }) {
                    A(AppPaths.PRIVACY, { classes(AppStylesLanding.navLink) }) { Text("Privacy") }
                    A(AppPaths.TERMS, { classes(AppStylesLanding.navLink) }) { Text("Terms") }
                    A(AppPaths.LEGAL, { classes(AppStylesLanding.navLink) }) { Text("Legal") }
                    A("#faq", { classes(AppStylesLanding.navLink) }) { Text("FAQ") }
                    A("mailto:appsyde.tb.help@gmail.com", { classes(AppStylesLanding.navLink) }) { Text("Contact") }
                }
            }
            Div({ classes(AppStylesLanding.footerDisclaimer) }) {
                Text("AppSyde — support par email, cohérent, sous contrôle.")
            }
        }
    }
}

/* ------------------------------ COMPONENTS ------------------------------ */

@Composable
private fun InfoCard(icon: String, title: String, body: String) {
    Div({ classes(AppStylesLanding.card) }) {
        Div({ classes(AppStylesLanding.cardTitleRow) }) {
            Div({ classes(AppStylesLanding.iconBubble) }) { Text(icon) }
            H3({ classes(AppStylesLanding.cardTitle) }) { Text(title) }
        }
        P({ classes(AppStylesLanding.cardBody) }) { Text(body) }
    }
}
